/**
 * Wild Faaji Countdown Timer Module
 * 
 * Provides countdown timer functionality for product drops and events.
 * Updates display every second and handles timezone conversion.
 * 
 * Requirements: 3.3, 4.4, 5.3
 */

/**
 * CountdownTimer class
 * Manages a single countdown timer instance
 */
class CountdownTimer {
  /**
   * Create a countdown timer
   * @param {Object} options - Configuration options
   * @param {HTMLElement} options.element - The countdown element to update
   * @param {string} options.endDate - ISO 8601 datetime string
   * @param {Function} [options.onComplete] - Callback when countdown reaches zero
   * @param {Function} [options.onTick] - Callback on each second update
   */
  constructor(options) {
    if (!options.element) {
      throw new Error('CountdownTimer requires an element');
    }

    if (!options.endDate) {
      throw new Error('CountdownTimer requires an endDate');
    }

    this.element = options.element;
    this.endDate = new Date(options.endDate);
    this.onComplete = options.onComplete || null;
    this.onTick = options.onTick || null;
    this.intervalId = null;
    this.isComplete = false;

    // Get display elements
    this.daysElement = this.element.querySelector('[data-countdown-days]');
    this.hoursElement = this.element.querySelector('[data-countdown-hours]');
    this.minutesElement = this.element.querySelector('[data-countdown-minutes]');
    this.secondsElement = this.element.querySelector('[data-countdown-seconds]');
    this.statusElement = this.element.querySelector('[data-countdown-status]');

    // Check if date is valid
    if (isNaN(this.endDate.getTime())) {
      this.showError('Invalid date format');
      return;
    }

    // Initialize and start
    this.update();
    this.start();
  }

  /**
   * Calculate time remaining
   * @returns {Object} Time components (days, hours, minutes, seconds, total)
   */
  calculateTimeRemaining() {
    const now = new Date();
    const timeDiff = this.endDate - now;

    if (timeDiff <= 0) {
      return {
        total: 0,
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
      };
    }

    const seconds = Math.floor((timeDiff / 1000) % 60);
    const minutes = Math.floor((timeDiff / 1000 / 60) % 60);
    const hours = Math.floor((timeDiff / (1000 * 60 * 60)) % 24);
    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));

    return {
      total: timeDiff,
      days,
      hours,
      minutes,
      seconds,
    };
  }

  /**
   * Format number with leading zero
   * @param {number} num - Number to format
   * @returns {string} Formatted number
   */
  formatNumber(num) {
    return String(num).padStart(2, '0');
  }

  /**
   * Update the countdown display
   */
  update() {
    const time = this.calculateTimeRemaining();

    if (time.total <= 0) {
      this.complete();
      return;
    }

    // Update display elements if they exist
    if (this.daysElement) {
      this.daysElement.textContent = this.formatNumber(time.days);
    }
    if (this.hoursElement) {
      this.hoursElement.textContent = this.formatNumber(time.hours);
    }
    if (this.minutesElement) {
      this.minutesElement.textContent = this.formatNumber(time.minutes);
    }
    if (this.secondsElement) {
      this.secondsElement.textContent = this.formatNumber(time.seconds);
    }

    // Call onTick callback if provided
    if (this.onTick && typeof this.onTick === 'function') {
      this.onTick(time);
    }
  }

  /**
   * Start the countdown timer
   */
  start() {
    // Don't start if already complete
    if (this.isComplete) {
      return;
    }

    // Clear any existing interval
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }

    // Update every second
    this.intervalId = setInterval(() => {
      this.update();
    }, 1000);
  }

  /**
   * Stop the countdown timer
   */
  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  /**
   * Handle countdown completion
   */
  complete() {
    if (this.isComplete) {
      return;
    }

    this.isComplete = true;
    this.stop();

    // Determine if event is live or ended
    const now = new Date();
    const isLiveNow = Math.abs(this.endDate - now) < 60000; // Within 1 minute

    // Update element state
    this.element.classList.remove('wf-countdown--active');
    this.element.classList.add('wf-countdown--ended');

    // Show appropriate status message
    if (this.statusElement) {
      this.statusElement.textContent = isLiveNow ? 'LIVE NOW' : 'ENDED';
    } else {
      // If no status element, update the entire countdown display
      const statusText = isLiveNow ? 'LIVE NOW' : 'ENDED';
      this.element.innerHTML = `
        <div class="wf-countdown__status wf-countdown__status--ended">
          <span class="wf-countdown__status-text" data-countdown-status>
            ${statusText}
          </span>
        </div>
      `;
    }

    // Call onComplete callback if provided
    if (this.onComplete && typeof this.onComplete === 'function') {
      this.onComplete();
    }

    // Dispatch custom event
    this.element.dispatchEvent(
      new CustomEvent('countdown:complete', {
        bubbles: true,
        detail: { endDate: this.endDate },
      })
    );
  }

  /**
   * Show error state
   * @param {string} message - Error message
   */
  showError(message) {
    console.error('CountdownTimer error:', message);
    this.element.innerHTML = `
      <div class="wf-countdown__status wf-countdown__status--error">
        <span class="wf-countdown__status-text">
          ENDED
        </span>
      </div>
    `;
    this.element.classList.add('wf-countdown--error');
  }

  /**
   * Destroy the countdown timer
   */
  destroy() {
    this.stop();
    this.element = null;
    this.onComplete = null;
    this.onTick = null;
  }
}

/**
 * Initialize all countdown timers on the page
 * @param {string} [selector='[data-countdown]'] - CSS selector for countdown elements
 * @returns {Array<CountdownTimer>} Array of initialized countdown timers
 */
function initCountdowns(selector = '[data-countdown]') {
  const countdownElements = document.querySelectorAll(selector);
  const timers = [];

  countdownElements.forEach((element) => {
    try {
      const endDate = element.getAttribute('data-end-date');

      if (!endDate) {
        console.warn('Countdown element missing data-end-date attribute', element);
        return;
      }

      // Check if timer already initialized
      if (element.hasAttribute('data-countdown-initialized')) {
        return;
      }

      // Mark as initialized
      element.setAttribute('data-countdown-initialized', 'true');

      // Create timer instance
      const timer = new CountdownTimer({
        element,
        endDate,
      });

      timers.push(timer);
    } catch (error) {
      console.error('Error initializing countdown timer:', error, element);
    }
  });

  return timers;
}

/**
 * Initialize countdowns when DOM is ready
 */
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initCountdowns();
    });
  } else {
    // DOM already loaded
    initCountdowns();
  }
}

// Export for use in other modules and tests
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { CountdownTimer, initCountdowns };
}

// Also expose globally for Shopify theme usage
if (typeof window !== 'undefined') {
  window.WildFaaji = window.WildFaaji || {};
  window.WildFaaji.CountdownTimer = CountdownTimer;
  window.WildFaaji.initCountdowns = initCountdowns;
}
